import React, { useEffect, useState } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  isDateSearchEnabled,
  isMainSearchTypeKeywords,
  isOriginInUse,
} from '../../../util/search';
import { DATE_MODE_QUERY_PARAM } from '../../../util/searchDates';
import {
  readStoredWeddingDate,
  weddingDateFromSearch,
  weddingDateSearchParams,
} from '../../../util/weddingDate';
import {
  parseRegionParam,
  regionParamValue,
  regionSearchMode,
  REGION_FIELD_KEY,
} from '../../../util/regions';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarAccountMenu from './TopbarAccountMenu/TopbarAccountMenu';
import TopbarCategoryRail from './TopbarCategoryRail/TopbarCategoryRail';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarMobileSearchBar from './TopbarMobileSearchBar/TopbarMobileSearchBar';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import TopbarWishlistLink from './TopbarWishlistLink/TopbarWishlistLink';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    onSaveWeddingDate,
    onSyncWeddingDate,
  } = props;

  // Whether the phone search bar is on screen. It folds away with the category rail and scrolls
  // off with the search page, so the sticky row brings the magnifier back while it is gone.
  const [isMobileSearchBarInView, setIsMobileSearchBarInView] = useState(true);

  // Read after mount: the server render knows neither the browser's storage nor the user. Read
  // again on every navigation, because the search page's prompt can save a date too.
  const [weddingDate, setWeddingDate] = useState(null);
  const isWeddingDateEnabled = isDateSearchEnabled(config);
  const currentUserId = currentUser?.id?.uuid;
  useEffect(() => {
    if (!isWeddingDateEnabled) {
      return;
    }
    if (currentUser) {
      onSyncWeddingDate(currentUser);
    }
    setWeddingDate(readStoredWeddingDate());
  }, [isWeddingDateEnabled, currentUserId, location.search]);

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    // Whatever Wann holds when the couple searches becomes their wedding date; an empty Wann
    // forgets it. A range can only come from a link, and is not a wedding date.
    if (isWeddingDateEnabled) {
      const picked = weddingDateFromSearch(values?.dates, values?.[DATE_MODE_QUERY_PARAM]);
      if (picked || !values?.dates) {
        onSaveWeddingDate(picked);
        setWeddingDate(picked);
      }
    }

    const topbarSearchParams = () => {
      // Null clears: stringify in util/urlHelpers drops a null param, so "we're still
      // flexible" takes the date out of the URL rather than leaving a stale one behind.
      const datesMaybe = isDateSearchEnabled(config)
        ? {
            dates: values?.dates || null,
            [DATE_MODE_QUERY_PARAM]: values?.[DATE_MODE_QUERY_PARAM] || null,
          }
        : {};

      // Built from the field's configured search mode rather than a mode picked here, so the
      // header and the sidebar filter produce the same URL for the same canton.
      const regionMaybe = {
        [`pub_${REGION_FIELD_KEY}`]: regionParamValue(values?.region, regionSearchMode(config)),
      };

      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords, ...datesMaybe, ...regionMaybe };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
        ...datesMaybe,
        ...regionMaybe,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    const target = createResourceLocatorString(
      routeName,
      routeConfiguration,
      pathParams,
      searchParams
    );

    // Clearing something that was already empty - "Alle Regionen" with nothing ticked, "Wir sind
    // noch flexibel" with no date - resolves every param to null, and stringify drops nulls. The
    // target is then the page the reader is already on. Pushing it stacked one duplicate history
    // entry per click, so the back button needed as many presses to escape as they had made
    // attempts.
    if (target === `${location.pathname}${location.search}`) {
      return;
    }

    history.push(target);
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - if only provider role – sales
   * - if only customer role – orders
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const parsedSearchParams = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { mobilemenu, mobilesearch, keywords, address, origin, bounds, dates } = parsedSearchParams;
  const dateMode = parsedSearchParams[DATE_MODE_QUERY_PARAM];

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const isSearchLink = link => {
    const routeName = link?.route?.name;
    if (routeName === 'SearchPage' || routeName === 'SearchPageWithListingType') {
      return true;
    }
    const href = link?.href;
    if (typeof href !== 'string') {
      return false;
    }
    const normalizedHref = href.toLowerCase();
    return (
      normalizedHref === '/s' ||
      normalizedHref.startsWith('/s?') ||
      normalizedHref.startsWith('/s/') ||
      normalizedHref === '/search' ||
      normalizedHref.startsWith('/search?') ||
      normalizedHref.startsWith('/search/')
    );
  };
  const customerFilteredCustomLinks =
    isCustomer && !showCreateListingsLink
      ? customLinks.filter(link => !isSearchLink(link))
      : customLinks;
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  // Every search the couple lands on without a date gets theirs, however they got there. Only
  // Wann changes or clears it, so "reset all" and an undated link both bring it straight back.
  const weddingDateParams = weddingDateSearchParams(weddingDate);
  const isSearchPage = ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const applyWeddingDate = isWeddingDateEnabled && isSearchPage && !dates && !!weddingDateParams;
  useEffect(() => {
    if (applyWeddingDate) {
      const search = stringify({ ...parse(location.search), ...weddingDateParams });
      history.replace(`${location.pathname}?${search}`, location.state);
    }
  }, [applyWeddingDate, location.search]);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customerFilteredCustomLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
      isCustomer={isCustomer}
      isProvider={isProvider}
    />
  );

  const topbarSearcInitialValues = () => {
    // The date segment shows what the URL already asks for, so arriving on a search page with
    // ?dates= opens the header on that date rather than blank. Without one it shows the saved
    // wedding date.
    const datesMaybe = !isDateSearchEnabled(config)
      ? {}
      : dates || !weddingDateParams
      ? { dates, [DATE_MODE_QUERY_PARAM]: dateMode }
      : weddingDateParams;
    const region = parseRegionParam(parsedSearchParams[`pub_${REGION_FIELD_KEY}`]);

    if (isMainSearchTypeKeywords(config)) {
      return { keywords, region, ...datesMaybe };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      region,
      ...datesMaybe,
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  // In pill mode phones get the bar, and the icon only while the bar is off screen: the icon
  // alone went unnoticed. Any other search type has no pill to summarise, so it keeps the icon.
  const showMobileSearchBar = showSearchForm && isMainSearchTypeKeywords(config);
  const openMobileSearch = () => redirectToURLWithModalState(history, location, 'mobilesearch');

  const mobileSearchBarMaybe = showMobileSearchBar ? (
    <TopbarMobileSearchBar
      dates={initialSearchFormValues.dates}
      dateMode={initialSearchFormValues[DATE_MODE_QUERY_PARAM]}
      regions={initialSearchFormValues.region}
      showDates={isDateSearchEnabled(config)}
      onOpen={openMobileSearch}
      onInViewChange={setIsMobileSearchBarInView}
    />
  ) : null;

  const showMobileSearchIcon = showSearchForm && (!showMobileSearchBar || !isMobileSearchBarInView);
  const mobileSearchButtonMaybe = showMobileSearchIcon ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={openMobileSearch}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon
        className={css.searchMenuIcon}
        ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      />
    </Button>
  ) : null;

  return (
    <>
      <div className={classes}>
        <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <nav className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <div className={css.mobileGroup}>
          <Button
            rootClassName={css.menu}
            onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <span className={css.menuIconWrapper}>
              <MenuIcon
                className={css.menuIcon}
                ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
              />
              {notificationDot}
            </span>
          </Button>
          {mobileSearchButtonMaybe}
        </div>
        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <div className={classNames(css.mobileGroup, css.mobileGroupRight)}>
          <TopbarWishlistLink intl={intl} />
          <TopbarAccountMenu
            currentPage={resolvedCurrentPage}
            currentUser={currentUser}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
            showManageListingsLink={showCreateListingsLink}
            notificationCount={notificationCount}
            inboxTab={topbarInboxTab}
            intl={intl}
          />
        </div>
      </nav>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customerFilteredCustomLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
          isProvider={isProvider}
          isCustomer={isCustomer}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        closeButtonClassName={css.mobileMenuClose}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
      </div>
      {/* Outside the topbar's own box on purpose: the bar is sticky and the rail
          is not, so the rail has to be a sibling rather than a child. One instance
          serves both breakpoints - the layout differs by CSS, not by branching. */}
      <TopbarCategoryRail>{mobileSearchBarMaybe}</TopbarCategoryRail>
    </>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
